/**
 * API utility with automatic token refresh on expiration
 * On 401: attempts silent token refresh before triggering logout
 */

import AppAuth from './AppAuth.js';

let logoutCallback = null;
let refreshPromise = null; // singleton to prevent concurrent refreshes
let authInstance = null;

// Register logout callback from AuthContext
export function setLogoutCallback(callback) {
  logoutCallback = callback;
}

// Register the AppAuth instance for token refresh
export function setAuthInstance(instance) {
  authInstance = instance;
}

/**
 * Attempt to refresh the token. Returns true if refresh succeeded.
 * Uses a singleton promise to prevent multiple concurrent refreshes.
 */
async function attemptTokenRefresh() {
  if (!authInstance) return false;
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const newToken = await authInstance.tryRefreshToken();
      return !!newToken;
    } catch (error) {
      if (error.retryable) throw error;
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Enhanced fetch that handles authentication errors with automatic token refresh
 */
export async function apiFetch(url, options = {}) {
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
    });

    // On 401, try to refresh the token and retry the request once
    if (response.status === 401) {
      console.log('[api] 401 received, attempting token refresh...');

      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        console.log('[api] Token refreshed, retrying request...');
        // Retry the original request with the new token
        const retryResponse = await fetch(url, {
          ...options,
          credentials: 'include',
        });
        if (retryResponse.status !== 401) {
          return retryResponse;
        }
      }

      // Refresh failed or retry still 401 — log out
      console.log('[api] Token refresh failed, logging out...');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');

      if (logoutCallback) {
        logoutCallback();
      } else {
        window.location.href = '/login';
      }

      throw Object.assign(new Error('Session expired — please login again'), { status: 401 });
    }

    if (response.status === 403) {
      let message = 'Forbidden';
      try {
        const payload = await response.clone().json();
        message = payload?.message || payload?.error || message;
      } catch {}
      throw Object.assign(new Error(message), { status: 403 });
    }

    return response;
  } catch (error) {
    throw error;
  }
}

/**
 * Helper function for JSON API calls
 */
export async function apiRequest(url, options = {}) {
  const response = await apiFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null;
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  return response.text();
}

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  get: (url, options = {}) => apiRequest(url, { ...options, method: 'GET' }),
  post: (url, data, options = {}) => apiRequest(url, { ...options, method: 'POST', body: JSON.stringify(data) }),
  put: (url, data, options = {}) => apiRequest(url, { ...options, method: 'PUT', body: JSON.stringify(data) }),
  delete: (url, options = {}) => apiRequest(url, { ...options, method: 'DELETE' }),
  patch: (url, data, options = {}) => apiRequest(url, { ...options, method: 'PATCH', body: JSON.stringify(data) }),
};

export default api;
